import { useNavigate } from 'react-router-dom';
import { useTranslations } from '../../i18n';
import { theme } from '../../lib/theme';
import { Bell, BellOff, Check } from 'lucide-react';
import { Reminder } from '../../types/reminders';
import { SupportedLanguage } from '../../lib/expenseTypeHelpers';
import { usePreferences } from '../../contexts/PreferencesContext';
import { formatTime } from '../../lib/timeUtils';

interface ReminderItemProps {
    reminder: Reminder;
    slug?: string;
    isAdminView?: boolean;
    compact?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    onCheck?: (e: React.MouseEvent) => void;
    className?: string;
    highlighted?: boolean;
    showCheckmark?: boolean;
}

export function ReminderItem({
    reminder,
    slug,
    isAdminView = false,
    compact = false,
    onClick,
    onCheck,
    className = '',
    highlighted = false,
    showCheckmark = false
}: ReminderItemProps) {
    const navigate = useNavigate();
    const { t, language } = useTranslations();
    const { preferences } = usePreferences();

    const navigateToReminder = (e: React.MouseEvent) => {
        if (onClick) {
            onClick(e);
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const prefix = isAdminView ? '/admin' : '';
        if (slug) {
            // Determine where to navigate. If it's attached to Note/Part, maybe go there?
            // For now, let's assume a dedicated edit view or just open the parent.
            let url = `${prefix}/${slug}/reminders/${reminder.id}`;
            if (reminder.note_id) {
                url = `${prefix}/${slug}/notes/${reminder.note_id}`;
            } else if (reminder.part_id) {
                url = `${prefix}/${slug}/parts/${reminder.part_id}`;
            }
            navigate(url);
        }
    };

    const getRelativeTime = (dateStr: string) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffInMs = date.getTime() - now.getTime();
        const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
        const diffInMonths = Math.ceil(diffInDays / 30);

        if (diffInDays === 0) return t.reminders.today;
        if (diffInDays === 1) return t.reminders.tomorrow;
        if (diffInDays === -1) return t.reminders.yesterday;

        if (Math.abs(diffInDays) < 30) {
            return diffInDays > 0 
                ? t.reminders.inXDays.replace('{x}', diffInDays.toString())
                : t.reminders.xDaysAgo.replace('{x}', Math.abs(diffInDays).toString());
        }

        return diffInMonths > 0 
            ? t.reminders.inXMonths.replace('{x}', diffInMonths.toString())
            : t.reminders.xMonthsAgo.replace('{x}', Math.abs(diffInMonths).toString());
    };

    return (
        <div className="relative group">
            <button
                type="button"
                onClick={navigateToReminder}
                className={`w-full text-left flex items-start gap-4 ${compact ? 'p-4' : theme.spacing.cardPadding} transition-all ${reminder.action_required ? 'bg-green-50 dark:bg-green-500/10 animate-remind-pulse shadow-sm shadow-green-500/10 ring-1 ring-green-200 dark:ring-green-900/40' : highlighted ? 'bg-yellow-50 dark:bg-yellow-500/10' : 'hover:bg-gray-50 dark:hover:bg-white/5'} active:scale-[0.99] rounded-xl ${className}`}
            >
                <div className={`${theme.spacing.iconContainer} rounded-full ${reminder.action_required ? 'bg-green-100 dark:bg-green-800/40' : reminder.is_active ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-100 dark:bg-gray-800/40'} flex items-center justify-center flex-shrink-0`}>
                    {reminder.is_active ? (
                        <Bell size={20} className={`${reminder.action_required ? 'text-green-700 dark:text-green-300' : 'text-green-600 dark:text-green-400'}`} />
                    ) : (
                        <BellOff size={20} className="text-gray-400 dark:text-gray-500" />
                    )}
                </div>

                <div className="flex-1 min-w-0 flex items-start gap-3">
                    <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                            <h4 className={`${theme.typography.itemTitle} truncate`}>
                                {reminder.name || (reminder.note_id ? t.reminders.noteReminder : (reminder.part_id ? t.reminders.partReminder : t.reminders.reminder))}
                            </h4>
                        </div>

                        {(() => {
                            const desc = reminder.description;
                            if (desc) return (
                                <p className="text-[13px] text-gray-500 dark:text-gray-400 line-clamp-1 italic">
                                    {desc}
                                </p>
                            );
                            
                            if (reminder.note_id && reminder.notes) {
                                const noteContent = reminder.notes.content || '';
                                const lines = noteContent.split('\n');
                                const firstLine = lines[0] || '';
                                const noteTitle = firstLine.replace(/[#*_`~]/g, '').trim().substring(0, 100);
                                if (noteTitle) return (
                                    <p className="text-[13px] text-gray-500 dark:text-gray-400 line-clamp-1 italic font-medium">
                                        {noteTitle}
                                    </p>
                                );
                            }
                            return null;
                        })()}

                    </div>

                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0 pt-0.5">
                        <span className={`${theme.typography.itemSubtitle} whitespace-nowrap`}>
                            {(() => {
                                const d = new Date(reminder.remind_date);
                                const isCurrentYear = d.getFullYear() === new Date().getFullYear();
                                return d.toLocaleDateString(language as SupportedLanguage, { 
                                    day: '2-digit', 
                                    month: 'short',
                                    year: isCurrentYear ? undefined : 'numeric'
                                });
                            })()}
                            {reminder.remind_time && <span className="text-gray-400 font-normal ml-1">
                                {formatTime(reminder.remind_time, preferences)}</span>}
                        </span>

                        {reminder.is_active && (
                            <span className="text-[12px] text-gray-500 dark:text-gray-400">
                                {getRelativeTime(reminder.remind_date)}
                            </span>
                        )}

                        {reminder.repeat_yearly && (
                            <span className="text-[10px] font-bold text-blue-500 dark:text-blue-400 uppercase tracking-widest mt-0.5">
                                {t.reminders.yearly}
                            </span>
                        )}
                    </div>
                </div>
            </button>

            {showCheckmark && (
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCheck?.(e);
                    }}
                    className="absolute -right-2 -top-2 w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center shadow-lg hover:bg-green-600 transition-all active:scale-95 z-10"
                    title={t.reminders.markSeen}
                >
                    <Check size={18} strokeWidth={3} />
                </button>
            )}
        </div>
    );
}
